/**
 * The partner colour palette, and how each colour is drawn.
 *
 * Mirrors `config/partnerColors.js` on the server key-for-key, in the same
 * order: the server stores and validates the key, this file owns the shade. A
 * key added on one side and not the other is either refused on save or drawn
 * in neutral grey, so keep the two lists together.
 *
 * The colour marks *whose* job something is — a stripe on an order row or a
 * planning card, a dot beside the client's name — and nothing else. It is never
 * a fill and never a status, which is why there is no pure red, green or amber
 * here: those already mean status on the same screens.
 */
export const PARTNER_COLORS = [
  { key: "teal", hex: "#1F9E89" },
  { key: "cyan", hex: "#1AA3B8" },
  { key: "indigo", hex: "#5361C9" },
  { key: "violet", hex: "#8A57C7" },
  { key: "plum", hex: "#A64CA0" },
  { key: "magenta", hex: "#D14B8F" },
  { key: "rose", hex: "#E0707F" },
  { key: "brown", hex: "#8C6A4A" },
  { key: "olive", hex: "#7F9138" },
  { key: "slate", hex: "#5F7A8A" },
  { key: "navy", hex: "#2F4A7C" },
  { key: "charcoal", hex: "#4A4A45" },
] as const;

export type PartnerColor = (typeof PARTNER_COLORS)[number]["key"];

/** A partner with no colour chosen: still a partner, just untagged. */
export const PARTNER_NEUTRAL_HEX = "#CFCBBE";

const HEX_BY_KEY = new Map<string, string>(
  PARTNER_COLORS.map((color) => [color.key, color.hex]),
);

/** The shade for a palette key; neutral grey for none or an unknown key. */
export const partnerColorHex = (key: string | null | undefined): string =>
  (key && HEX_BY_KEY.get(key)) || PARTNER_NEUTRAL_HEX;

/**
 * The stripe for something whose client may or may not be a partner.
 *
 * Null — no stripe at all — when the client is not a directory partner (typed
 * in freely), because a grey stripe would claim a partner that is not there.
 * A partner with no colour chosen gets the neutral grey.
 */
export const partnerStripe = (
  partner: { color?: string | null } | null | undefined,
): string | null => (partner ? partnerColorHex(partner.color) : null);
